import { createContext, useEffect, useReducer, ReactElement } from 'react';

// third-party
import AWS from 'aws-sdk';
import { CognitoUser, CognitoUserPool, CognitoUserSession, CognitoUserAttribute, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { v4 as uuid } from 'uuid';
import { format } from 'date-fns';

// project-imports
import Loader from 'components/Loader';
import { USER_CONTEXT_CODE, USER_COUNTRY_CODE } from 'components/Locales';
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';
import { dispatch as dispatchStore } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
// action-manager
import { APIAction } from 'utils/action-manager/api/apiManager';
import { HubspotAction } from 'utils/action-manager/hubspot/hubspotManager';
import handleAction from 'utils/action-manager/actionManager';
import { getCountryMarketByContextCode } from 'utils/countryMarket';
import { isTrialExclPlan, TrialExclPlan } from 'utils/trialPlans';
import { getBrowserTimeZoneCountry } from 'utils/timezoneCountry';

// types
import { AWSCognitoContextType, InitialLoginContextProps, UserDetailsOptions, UserProfile } from 'types/auth';

interface UserAttributes {
  [key: string]: string;
}

const getCountryFromContextCode = (code?: string | null) => getCountryMarketByContextCode(code)?.countryCode || '';

// constant
const initialState: InitialLoginContextProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

export const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_AWS_POOL_ID || '',
  ClientId: process.env.REACT_APP_AWS_APP_CLIENT_ID || ''
});

const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
  } else {
    localStorage.removeItem('serviceToken');
  }
};

const setToken = (idToken?: string | null) => {
  if (idToken) {
    localStorage.setItem('idToken', idToken);
  } else {
    localStorage.removeItem('idToken');
  }
};

AWS.config.update({
  // accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY || '',
  // secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY || '',
  region: 'eu-west-3'
});

// ==============================|| AWS COGNITO - CONTEXT & PROVIDER ||============================== //

const AWSCognitoContext = createContext<AWSCognitoContextType | null>(null);
const HUBSPOT_REGISTER_ENABLED = true;
const USER_DETAILS_CACHE_TTL_MS = 60 * 1000;
const userDetailsCache = new Map<string, { timestamp: number; value: any }>();
const userDetailsInFlight = new Map<string, Promise<any>>();

const getUserDetailsCacheKey = (username: string, includeChildren: boolean) =>
  `${username.trim().toLowerCase()}::${includeChildren ? 'children' : 'self'}`;

export const AWSCognitoProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const getMailLocale = (locale?: string) => (locale?.startsWith('es') ? 'es' : 'en');

  const sendWelcomePaid = async (to: string, userName: string, locale: string) => {
    const apiAction: APIAction = {
      type: 'api',
      entity: 'mail',
      method: 'welcome-paid',
      data: {
        to,
        user_name: userName,
        locale
      }
    };

    return handleAction(apiAction);
  };

  const sendWelcomeFreemium = async (to: string, userName: string, locale: string, promotionsCount: string) => {
    const apiAction: APIAction = {
      type: 'api',
      entity: 'mail',
      method: 'welcome-freemium',
      data: {
        to,
        user_name: userName,
        locale,
        promotions_count: promotionsCount
      }
    };

    return handleAction(apiAction);
  };

  const getResumeDevelopmentsCount = async () => {
    const apiAction: APIAction = {
      type: 'api',
      entity: 'metrics',
      method: 'resume'
    };

    const response: any = await handleAction(apiAction);
    return String(response?.stats?.developmentscount ?? 0);
  };

  const handleFirstLoginWelcome = async (params: {
    firstLogin?: string;
    currentPlan?: string;
    email: string;
    firstname?: string;
    locale?: string;
  }) => {
    const { firstLogin, currentPlan, email, firstname, locale } = params;

    if (firstLogin === 'true') return;

    await updateProfile({ 'custom:first_login': 'true' });

    const mailLocale = getMailLocale(locale);
    const userName = firstname || '';

    try {
      if (isTrialExclPlan(currentPlan)) {
        const promotionsCount = await getResumeDevelopmentsCount();
        await sendWelcomeFreemium(email, userName, mailLocale, promotionsCount);
      }

      if (currentPlan === 'pro') {
        await sendWelcomePaid(email, userName, mailLocale);
      }
    } catch (err) {
      console.error('Welcome email failed', err);
    }
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      const serviceToken = window.localStorage.getItem('serviceToken');
      if (!serviceToken) {
        dispatch({ type: LOGOUT });
        return;
      }

      setSession(serviceToken);
      const currentUser = userPool.getCurrentUser();

      if (!currentUser) {
        dispatch({ type: LOGOUT });
        return;
      }

      currentUser.getSession(async (err: any, session: any) => {
        if (err || !session || !session.isValid()) {
          console.warn('Session expired or invalid, trying to refresh...');

          // Intentamos refresh
          currentUser.refreshSession(session?.getRefreshToken(), (refreshErr, newSession) => {
            if (refreshErr) {
              console.error('Failed to refresh session:', refreshErr);
              clearCognitoStorage();
              dispatch({ type: LOGOUT });
              return;
            }

            console.log('Session refreshed successfully');
            setSession(newSession.getAccessToken().getJwtToken());

            // Notifications
            setToken(newSession.getIdToken().getJwtToken());

            // Después de refrescar, volvemos a intentar cargar userAttributes
            currentUser.getSession((err2: any, refreshedSession: any) => {
              if (err2 || !refreshedSession || !refreshedSession.isValid()) {
                console.error('Failed to retrieve session after refresh');
                dispatch({ type: LOGOUT });
                return;
              }

              // Ahora cargamos atributos
              loadUserAttributes(currentUser);
            });
          });
        } else {
          setSession(session.getAccessToken().getJwtToken());
          setToken(session.getIdToken().getJwtToken());

          // Si la sesión era válida desde el principio
          loadUserAttributes(currentUser);
        }
      });
    } catch (err) {
      console.error('init() global error:', err);
      dispatch({ type: LOGOUT });
    }
  };

  // Función auxiliar para no repetir el código de cargar atributos
  const loadUserAttributes = (currentUser: CognitoUser) => {
    currentUser.getUserAttributes(async (error, attributes) => {
      if (error || !attributes) {
        console.error('Failed to load user attributes:', error);
        dispatch({ type: LOGOUT });
        return;
      }

      const userAttributes = attributes.reduce<Record<string, string>>((acc, attribute) => {
        acc[attribute.getName()] = attribute.getValue();
        return acc;
      }, {});

      const id = userAttributes['sub'];
      const email = userAttributes['email'];
      const firstname = userAttributes['name'];
      const lastname = userAttributes['family_name'] || '';
      const username = `${firstname} ${lastname}`;
      const phone = userAttributes['phone_number'] || '';
      const role = userAttributes['custom:role'];
      const currentPlan = userAttributes['custom:current_plan'];
      const subscriptionId = userAttributes['custom:st_sub_id'];
      const appProducts = userAttributes['custom:app_products'];
      const partnerApps = userAttributes['custom:partner_apps'];
      const isCompany = userAttributes['custom:is_company'];
      const companyName = userAttributes['custom:company_name'];
      const taxName = userAttributes['custom:tax_name'];
      const documentId = userAttributes['custom:document_id'];
      const stripeCustomerId = userAttributes['custom:stripe_customer_id'];
      const trialEndDate = userAttributes['custom:trial_end_date'];
      const sessionDetails = userAttributes['custom:login_details'];
      const devComAccess = userAttributes['custom:dev_com_access'];
      const logo = userAttributes['custom:company_logo'];
      const logoLight = userAttributes['custom:company_logo_light'];
      const firstLogin = userAttributes['custom:first_login'];
      const lastLogin = userAttributes['custom:last_login'];
      const code = userAttributes['custom:code'];
      const offer = userAttributes['custom:offer'];
      const commercial = userAttributes['custom:commercial'];
      const country = userAttributes['custom:country'];
      const forceYearly = userAttributes['custom:force_yearly'];
      const permissions = userAttributes['custom:permissions'];
      const notifSettings = userAttributes['custom:notif_settings'];

      if (!sessionDetails || sessionDetails !== localStorage.getItem('__suuid__')) {
        console.error('Session not available or mismatched');
        localStorage.removeItem('__suuid__');
        dispatch({ type: LOGOUT });
        return;
      }

      if (!role.includes('agent')) {
        console.error('Access denied: the user is not an agent');
        dispatch({ type: LOGOUT });
        return;
      }

      updateLastLogin();

      // --- NEW: si es hijo, trae detalles del padre y enriquece ---
      let parentExtra: Partial<UserProfile> = {};
      const parentAccountId = userAttributes['custom:parent_account_id'];

      if (parentAccountId) {
        try {
          // tu endpoint /user-details devuelve UserParent cuando llamas con el propio usuario
          const details = await userDetails(id /* o email si tu endpoint lo requiere */);
          const parent = details?.UserParent || null;
          const pua = parent?.UserAttributes || {};

          const parentAppProducts = pua['custom:app_products'] || '[]';
          parentExtra = {
            isChild: true,
            parentId: pua['sub'] || parentAccountId,
            parentEmail: pua['email'] || '',
            parentFirstname: pua['name'] || '',
            parentLastname: pua['family_name'] || '',
            parentCompanyName: pua['custom:company_name'] || '',
            parentCurrentPlan: pua['custom:current_plan'] || '',
            parentAppProducts,
            parentHasExtra: parseProducts(parentAppProducts).includes('extra_user'),
            parentDevComAccess: pua['custom:dev_com_access'],
            parentCode: pua['custom:code'],
            parentOffer: pua['custom:offer'],
            parentCountry: pua['custom:country']
          };
        } catch (e) {
          console.warn('userDetails (init) failed:', e);
        }
      }

      localStorage.setItem(USER_CONTEXT_CODE, parentExtra.parentCode || code || '');
      localStorage.setItem(USER_COUNTRY_CODE, parentExtra.parentCountry || country || '');

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: {
            id,
            email,
            firstname,
            lastname,
            name: username,
            phone,
            role,
            currentPlan,
            subscriptionId,
            appProducts,
            partnerApps,
            isCompany,
            companyName,
            taxName,
            documentId,
            stripeCustomerId,
            trialEndDate,
            sessionDetails,
            devComAccess,
            logo,
            logoLight,
            firstLogin,
            lastLogin,
            code,
            offer,
            commercial,
            country,
            forceYearly,
            notifSettings,
            // --- NEW ---
            ...parentExtra,
            permissions: permissions
          }
        }
      });
    });
  };

  const parseProducts = (p: unknown): string[] => {
    if (Array.isArray(p)) return p as string[];
    if (typeof p === 'string') {
      try {
        return JSON.parse(p);
      } catch {
        return [];
      }
    }
    return [];
  };

  const login = async (email: string, password: string) => {
    const usr = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    const authData = new AuthenticationDetails({
      Username: email,
      Password: password
    });

    return new Promise<void>((resolve, reject) => {
      usr.authenticateUser(authData, {
        onSuccess: (session: CognitoUserSession) => {
          setSession(session.getAccessToken().getJwtToken());

          // Notifications
          setToken(session.getIdToken().getJwtToken());

          usr.getUserAttributes(async (err, attributes) => {
            if (err || !attributes) {
              console.error(err);
              reject(new Error('Failed to retrieve user attributes'));
              return;
            }

            const attributesObject: UserAttributes = attributes.reduce<UserAttributes>((acc, current) => {
              const name = current.getName();
              const value = current.getValue();
              if (name && value) {
                acc[name] = value;
              }
              return acc;
            }, {});

            const id = attributesObject['sub'];
            const firstname = attributesObject['name'];
            const lastname = attributesObject['family_name'] || '';
            const username = `${firstname} ${lastname}`;
            const phone = attributesObject['phone_number'] || '';
            const role = attributesObject['custom:role'];
            const currentPlan = attributesObject['custom:current_plan'];
            const subscriptionId = attributesObject['custom:st_sub_id'];
            const appProducts = attributesObject['custom:app_products'];
            const partnerApps = attributesObject['custom:partner_apps'];
            const isCompany = attributesObject['custom:is_company'];
            const companyName = attributesObject['custom:company_name'];
            const taxName = attributesObject['custom:tax_name'];
            const documentId = attributesObject['custom:document_id'];
            const stripeCustomerId = attributesObject['custom:stripe_customer_id'];
            const trialEndDate = attributesObject['custom:trial_end_date'];
            const sessionDetails = attributesObject['custom:login_details'];
            const devComAccess = attributesObject['custom:dev_com_access'];
            const logo = attributesObject['custom:company_logo'];
            const logoLight = attributesObject['custom:company_logo_light'];
            const firstLogin = attributesObject['custom:first_login'];
            const lastLogin = attributesObject['custom:last_login'];
            const code = attributesObject['custom:code'];
            const offer = attributesObject['custom:offer'];
            const commercial = attributesObject['custom:commercial'];
            const country = attributesObject['custom:country'];
            const forceYearly = attributesObject['custom:force_yearly'];
            const permissions = attributesObject['custom:permissions'];
            const notifSettings = attributesObject['custom:notif_settings'];
            const locale = attributesObject['locale'] || 'es';

            await handleFirstLoginWelcome({
              firstLogin,
              currentPlan,
              email: authData.getUsername(),
              firstname,
              locale
            });

            if (!sessionDetails || !localStorage.getItem('__suuid__')) {
              console.error('Session not available');
              const newSession = uuid();
              localStorage.setItem('__suuid__', newSession);
              await updateProfile({ 'custom:login_details': newSession });
              // return;
            } else {
              // [ To-Do ] - Check if it is necessary to call login again (because if __suuid__ exists and is removed, the user needs to click again to log in)
              if (sessionDetails !== localStorage.getItem('__suuid__')) {
                console.error('Invalid session');
                localStorage.removeItem('__suuid__');
                dispatch({ type: LOGOUT });
                return;
              }
            }

            if (!role.includes('agent')) {
              console.error('Access denied: the user provided is not recognised in the corresponding user group');
              reject(new Error('Access denied: the user provided is not recognised in the corresponding user group'));
              dispatchStore(
                openSnackbar({
                  open: true,
                  message: 'Access denied: the user provided is not recognised in the corresponding user group',
                  variant: 'alert',
                  alert: {
                    color: 'error',
                    severity: 'error'
                  },
                  close: false,
                  setTimeout: 10
                })
              );
              return;
            }

            updateLastLogin();

            //  [ CURRENT PLAN MANAGEMENT ]
            if (!isTrialExclPlan(currentPlan)) {
              //    > If !PRO + subscriptions (stripe) > current_plan to pro (failed to upgrade in upgrade flow)
              if (currentPlan !== 'pro' && stripeCustomerId) {
                const apiAction: APIAction = {
                  type: 'api',
                  entity: 'stripe',
                  method: 'read-customer',
                  data: {
                    customer_id: stripeCustomerId
                  }
                };

                handleAction(apiAction)
                  .then(async (res: any) => {
                    if (res && res.subscriptions.length > 0) {
                      await updateProfile({ 'custom:current_plan': 'pro' });
                      window.location.reload();
                    }
                  })
                  .catch((err) => console.error(err));
              }
              //    > If PRO + !subscriptions (stripe) > current_plan to freemium international
              if (currentPlan === 'pro' && stripeCustomerId) {
                const apiAction: APIAction = {
                  type: 'api',
                  entity: 'stripe',
                  method: 'read-customer',
                  data: {
                    customer_id: stripeCustomerId
                  }
                };

                handleAction(apiAction)
                  .then(async (res: any) => {
                    if (res && res.subscriptions.length === 0) {
                      await updateProfile({ 'custom:current_plan': 'trial-excl' });
                    }
                  })
                  .catch((err) => console.error(err));
              }
              //    > If TRIAL + (current date > trial_end_date) -> current_plan to freemium international
              if (currentPlan === 'trial' && trialEndDate) {
                const dateNow = new Date();
                if (dateNow > new Date(trialEndDate)) {
                  await updateProfile({ 'custom:current_plan': 'trial-excl' });
                }
              }
            }

            // --- NEW: si es hijo, trae detalles del padre y enriquece ---
            let parentExtra: Partial<UserProfile> = {};
            const parentAccountId = attributesObject['custom:parent_account_id'];

            if (parentAccountId) {
              try {
                // tu endpoint /user-details devuelve UserParent cuando llamas con el propio usuario
                const details = await userDetails(id /* o email si tu endpoint lo requiere */);
                const parent = details?.UserParent || null;
                const pua = parent?.UserAttributes || {};

                const parentAppProducts = pua['custom:app_products'] || '[]';
                parentExtra = {
                  isChild: true,
                  parentId: pua['sub'] || parentAccountId,
                  parentEmail: pua['email'] || '',
                  parentFirstname: pua['name'] || '',
                  parentLastname: pua['family_name'] || '',
                  parentCompanyName: pua['custom:company_name'] || '',
                  parentCurrentPlan: pua['custom:current_plan'] || '',
                  parentAppProducts,
                  parentHasExtra: parseProducts(parentAppProducts).includes('extra_user'),
                  parentDevComAccess: pua['custom:dev_com_access'],
                  parentCode: pua['custom:code'],
                  parentOffer: pua['custom:offer'],
                  parentCountry: pua['custom:country']
                };
              } catch (e) {
                console.warn('userDetails (init) failed:', e);
              }
            }

            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user: {
                  id: id,
                  email: authData.getUsername(),
                  firstname: firstname,
                  lastname: lastname,
                  name: username,
                  phone: phone,
                  role: role,
                  currentPlan: currentPlan,
                  subscriptionId: subscriptionId,
                  appProducts: appProducts,
                  partnerApps: partnerApps,
                  isCompany: isCompany,
                  companyName: companyName,
                  taxName: taxName,
                  documentId: documentId,
                  stripeCustomerId: stripeCustomerId,
                  trialEndDate: trialEndDate,
                  sessionDetails: sessionDetails,
                  devComAccess: devComAccess,
                  logo: logo,
                  logoLight: logoLight,
                  firstLogin: firstLogin,
                  lastLogin: lastLogin,
                  code: code,
                  offer: offer,
                  commercial: commercial,
                  country: country,
                  forceYearly: forceYearly,
                  notifSettings: notifSettings,
                  // --- NEW ---
                  ...parentExtra,
                  permissions: permissions
                }
              }
            });
            localStorage.setItem(USER_CONTEXT_CODE, parentExtra.parentCode || code || '');
            localStorage.setItem(USER_COUNTRY_CODE, parentExtra.parentCountry || country || '');

            resolve();
          });
        },
        onFailure: (err) => {
          console.error('Authentication failed', err);
          reject(new Error('Incorrect username or password'));
        },
        newPasswordRequired: () => {
          // User was signed up by an admin and must provide new
          // password and required attributes, if any, to complete
          // authentication.
          // the api doesn't accept this field back
          // delete userAttributes.email_verified;
          // unsure about this field, but I don't send this back
          // delete userAttributes.phone_number_verified;
          // Get these details and call
          // usr.completeNewPasswordChallenge(password, userAttributes, requiredAttributes);
        }
      });
    });
  };

  const register = (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    isCompany: string,
    companyName: string,
    taxName: string,
    documentId: string,
    customerId: string,
    checkData: string,
    checkComms: string,
    trial: boolean = true,
    code: string,
    commercial: string,
    phone: string,
    isTrialExcl: boolean = false,
    locale: string = 'es',
    trialPlan?: TrialExclPlan
  ) =>
    new Promise((success, rej) => {
      const date = new Date();
      let trialEndDate: any = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
      trialEndDate.setUTCDate(trialEndDate.getUTCDate() + 4);
      const country = getCountryFromContextCode(code);
      const currentPlanValue = trialPlan || (isTrialExcl ? 'trial-excl' : trial ? 'trial' : 'pro');

      if (!trial) {
        trialEndDate = '';
      }

      userPool.signUp(
        email,
        password,
        [
          new CognitoUserAttribute({ Name: 'name', Value: firstName }),
          new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
          new CognitoUserAttribute({ Name: 'custom:role', Value: 'agent' }),
          new CognitoUserAttribute({ Name: 'custom:is_company', Value: isCompany }),
          new CognitoUserAttribute({ Name: 'custom:company_name', Value: companyName }),
          new CognitoUserAttribute({ Name: 'custom:tax_name', Value: taxName }),
          new CognitoUserAttribute({ Name: 'custom:document_id', Value: documentId }),
          new CognitoUserAttribute({ Name: 'custom:current_plan', Value: currentPlanValue }),
          new CognitoUserAttribute({ Name: 'custom:code', Value: code }),
          ...(country ? [new CognitoUserAttribute({ Name: 'custom:country', Value: country })] : []),
          new CognitoUserAttribute({ Name: 'custom:commercial', Value: commercial }),
          new CognitoUserAttribute({ Name: 'custom:stripe_customer_id', Value: customerId }),
          new CognitoUserAttribute({ Name: 'custom:trial_end_date', Value: trialEndDate }),
          new CognitoUserAttribute({ Name: 'custom:check_data', Value: checkData }),
          new CognitoUserAttribute({ Name: 'custom:check_comms', Value: checkComms }),
          new CognitoUserAttribute({ Name: 'phone_number', Value: phone }),
          new CognitoUserAttribute({ Name: 'locale', Value: locale })
        ],
        [],
        async (err, result) => {
          if (err) {
            rej(err);
            return;
          }

          // [ HUBSPOT ]
          const data: any = {
            firstname: firstName,
            lastname: lastName,
            email: email,
            company: companyName,
            pro: trial ? false : true,
            freemium: isTrialExclPlan(currentPlanValue),
            codeUrl: `https://agent.habihub.com/code-verification?user=${email}`,
            language: '',
            trialEndDate: trial ? format(trialEndDate, 'yyyy-MM-dd') : '',
            code: code || '',
            commercial: commercial || '',
            phone: phone,
            country: getBrowserTimeZoneCountry(),
            cognitoId: result?.userSub || ''
          };

          const hubspotAction: HubspotAction = {
            type: 'hubspot',
            method: 'register',
            data: data
          };

          if (HUBSPOT_REGISTER_ENABLED) {
            handleAction(hubspotAction)
              .then((res) => console.log(res))
              .catch((err) => console.error(err));
          }

          success(result);
        }
      );
    });

  const registerNew = (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    isCompany: string,
    companyName: string,
    taxName: string,
    documentId: string,
    customerId: string,
    checkData: string,
    checkComms: string,
    trial: boolean = true,
    code: string,
    commercial: string,
    phone: string,
    isTrialExcl: boolean = false,
    forceYearly: string = 'false',
    locale: string = 'es',
    trialPlan?: TrialExclPlan
  ) =>
    new Promise((success, rej) => {
      const date = new Date();
      let trialEndDate: any = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
      trialEndDate.setUTCDate(trialEndDate.getUTCDate() + 4);
      const country = getCountryFromContextCode(code);
      const currentPlanValue = trialPlan || (isTrialExcl ? 'trial-excl' : trial ? 'trial' : 'pro');

      if (!trial) {
        trialEndDate = '';
      }

      userPool.signUp(
        email,
        password,
        [
          new CognitoUserAttribute({ Name: 'name', Value: firstName }),
          new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
          new CognitoUserAttribute({ Name: 'custom:role', Value: 'agent' }),
          new CognitoUserAttribute({ Name: 'custom:is_company', Value: isCompany }),
          new CognitoUserAttribute({ Name: 'custom:company_name', Value: companyName }),
          new CognitoUserAttribute({ Name: 'custom:tax_name', Value: taxName }),
          new CognitoUserAttribute({ Name: 'custom:document_id', Value: documentId }),
          new CognitoUserAttribute({ Name: 'custom:current_plan', Value: currentPlanValue }),
          new CognitoUserAttribute({ Name: 'custom:code', Value: code }),
          ...(country ? [new CognitoUserAttribute({ Name: 'custom:country', Value: country })] : []),
          new CognitoUserAttribute({ Name: 'custom:commercial', Value: commercial }),
          new CognitoUserAttribute({ Name: 'custom:force_yearly', Value: forceYearly }),
          new CognitoUserAttribute({ Name: 'custom:stripe_customer_id', Value: customerId }),
          new CognitoUserAttribute({ Name: 'custom:trial_end_date', Value: trialEndDate }),
          new CognitoUserAttribute({ Name: 'custom:check_data', Value: checkData }),
          new CognitoUserAttribute({ Name: 'custom:check_comms', Value: checkComms }),
          new CognitoUserAttribute({ Name: 'phone_number', Value: phone }),
          new CognitoUserAttribute({ Name: 'locale', Value: locale })
        ],
        [],
        async (err, result) => {
          if (err) {
            rej(err);
            return;
          }

          // [ HUBSPOT ]
          const data: any = {
            firstname: firstName,
            lastname: lastName,
            email: email,
            company: companyName,
            pro: trial ? false : true,
            freemium: isTrialExclPlan(currentPlanValue),
            codeUrl: `https://agent.habihub.com/code-verification?user=${email}`,
            language: '',
            trialEndDate: trial ? format(trialEndDate, 'yyyy-MM-dd') : '',
            code: code || '',
            commercial: commercial || '',
            phone: phone,
            country: getBrowserTimeZoneCountry(),
            cognitoId: result?.userSub || ''
          };

          const hubspotAction: HubspotAction = {
            type: 'hubspot',
            method: 'register',
            data: data
          };

          if (HUBSPOT_REGISTER_ENABLED) {
            handleAction(hubspotAction)
              .then((res) => console.log(res))
              .catch((err) => console.error(err));
          }

          success(result);
        }
      );
    });

  const verifyAccount = async (email: string, verificationCode: string) => {
    const user = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    return new Promise((resolve, reject) => {
      user.confirmRegistration(verificationCode, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  const resendConfirmationCode = async (email: string) => {
    if (email === null || email === undefined) {
      return new Promise((_, reject) => {
        reject('Username and Pool information are required');
      });
    }

    const user = new CognitoUser({
      Username: email as string,
      Pool: userPool
    });

    return new Promise((resolve, reject) => {
      user.resendConfirmationCode((error, result) => {
        if (error) {
          reject(error.message || JSON.stringify(error));
          return;
        } else {
          resolve(result);
        }
      });
    });
  };

  // const logout = () => {
  //   const loggedInUser = userPool.getCurrentUser();
  //   if (loggedInUser) {
  //     setSession(null);
  //     loggedInUser.signOut();
  //     dispatch({ type: LOGOUT });
  //   }
  // };

  const logout = () => {
    const loggedInUser = userPool.getCurrentUser();
    localStorage.removeItem(USER_CONTEXT_CODE);
    localStorage.removeItem(USER_COUNTRY_CODE);
    if (loggedInUser) {
      setSession(null);
      loggedInUser.signOut();
      clearCognitoStorage();
      dispatch({ type: LOGOUT });
    }
  };

  const forgotPassword = async (email: string) => {
    const userData = {
      Username: email,
      Pool: userPool
    };

    const cognitoUser = new CognitoUser(userData);

    return new Promise<void>((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  };

  const resetPassword = async (verificationCode: string, newPassword: string, email: string) => {
    const userData = {
      Username: email,
      Pool: userPool
    };

    const cognitoUser = new CognitoUser(userData);

    return new Promise<void>((resolve, reject) => {
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err: any) => {
          if (err.code === 'CodeMismatchException') {
            reject('Invalid verification code. Please, try again');
          } else {
            reject(err.message || JSON.stringify(err));
          }
        }
      });
    });
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    const user = userPool.getCurrentUser();

    if (!user) {
      throw new Error('User is not authenticated');
    }

    return new Promise<void>((resolve: any, reject: any) => {
      user.getSession((err: any, session: any) => {
        if (err) {
          reject(err);
          return;
        }

        user.changePassword(oldPassword, newPassword, (error: any, result: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      });
    });
  };

  const updateCurrentUserAttributes = (attributes: { [key: string]: string }, reload: boolean = true) => {
    return new Promise<void>((resolve, reject) => {
      const user = userPool.getCurrentUser();

      if (user) {
        user.getSession((err: any, session: any) => {
          if (err) {
            reject(err);
            return;
          }

          const attributeList = [];
          for (const key in attributes) {
            attributeList.push(new CognitoUserAttribute({ Name: key, Value: attributes[key] }));
          }

          user.updateAttributes(attributeList, (err, result) => {
            if (err) {
              reject(err);
              return;
            }
            resolve();
            if (reload) {
              init();
            }
          });
        });
      } else {
        reject(new Error('User is not authenticated'));
      }
    });
  };

  const updateProfile = (attributes: { [key: string]: string }) => updateCurrentUserAttributes(attributes);

  const updateLastLogin = async () => {
    try {
      await updateCurrentUserAttributes({ 'custom:last_login': new Date().toISOString() }, false);
    } catch (err) {
      console.warn('Failed to update last login:', err);
    }
  };

  // const userDetails = async (username: string) => {
  //   const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider();
  //   const params = {
  //     UserPoolId: process.env.REACT_APP_AWS_POOL_ID || '',
  //     Username: username
  //   };

  //   try {
  //     const data = await cognitoIdentityServiceProvider.adminGetUser(params).promise();
  //     const userAttributes = mapAttributesToObject(data.UserAttributes || []);
  //     return { ...data, UserAttributes: userAttributes };
  //   } catch (error) {
  //     console.error(error);
  //     throw error;
  //   }
  // };

  const userDetails = async (username: string, options?: UserDetailsOptions) => {
    const includeChildren = options?.includeChildren ?? false;
    const cacheKey = getUserDetailsCacheKey(username, includeChildren);
    const cached = userDetailsCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < USER_DETAILS_CACHE_TTL_MS) return cached.value;
    if (userDetailsInFlight.has(cacheKey)) return userDetailsInFlight.get(cacheKey);

    const apiAction: APIAction = {
      type: 'api',
      entity: 'cognito',
      method: 'user-details',
      data: {
        username,
        includeChildren
      }
    };

    const request = (async () => {
      const res: any = await handleAction(apiAction);

      // console.log(res);

      userDetailsCache.set(cacheKey, { timestamp: Date.now(), value: res });

      return res;
      // return {
      //   Username: res.username,
      //   UserStatus: res.user_status,
      //   Enabled: res.enabled,
      //   UserAttributes: res.attributes
      // };
    })()
      .catch((err) => {
        console.error('userDetails error:', err);
        throw err;
      })
      .finally(() => {
        userDetailsInFlight.delete(cacheKey);
      });

    userDetailsInFlight.set(cacheKey, request);
    return request;
  };

  // const mapAttributesToObject = (attributes: AWS.CognitoIdentityServiceProvider.AttributeListType): { [key: string]: string } => {
  //   return attributes.reduce((acc, attribute) => {
  //     if (attribute.Value !== undefined) {
  //       acc[attribute.Name] = attribute.Value;
  //     }
  //     return acc;
  //   }, {} as { [key: string]: string });
  // };

  // const checkEmailExists = async (email: string): Promise<boolean> => {
  //   const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider();

  //   const params = {
  //     UserPoolId: process.env.REACT_APP_AWS_POOL_ID || '',
  //     Username: email
  //   };

  //   try {
  //     await cognitoIdentityServiceProvider.adminGetUser(params).promise();
  //     return true;
  //   } catch (error: any) {
  //     if (error.code === 'UserNotFoundException') {
  //       return false;
  //     }
  //     throw error;
  //   }
  // };

  const checkEmailExists = async (email: string): Promise<boolean> => {
    const apiAction: APIAction = {
      type: 'api',
      entity: 'cognito',
      method: 'check-email',
      data: { email }
    };

    try {
      const res: any = await handleAction(apiAction);
      return !!res?.registered;
    } catch (err) {
      console.error('checkEmailExists error:', err);
      return false;
    }
  };

  const verifySessionDetails = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const user = userPool.getCurrentUser();

      if (!user) {
        reject(new Error('User is not authenticated'));
        return;
      }

      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err || new Error('Failed to get session'));
          return;
        }

        const accessToken = session.getAccessToken().getJwtToken();
        const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider();
        const params = {
          AccessToken: accessToken
        };

        cognitoIdentityServiceProvider.getUser(params, (err: AWS.AWSError, data: AWS.CognitoIdentityServiceProvider.GetUserResponse) => {
          if (err) {
            reject(err);
            return;
          }

          const sessionDetails = data.UserAttributes?.find((attr) => attr.Name === 'custom:login_details')?.Value;

          const localSessionUUID = localStorage.getItem('__suuid__');

          if (!sessionDetails || sessionDetails !== localSessionUUID) {
            console.error('Session not available');
            localStorage.removeItem('__suuid__');
            dispatch({ type: LOGOUT });
            reject(new Error('Session mismatch'));
          } else {
            resolve();
          }
        });
      });
    });
  };

  // 🔹 Función auxiliar para limpiar los datos de Cognito en localStorage SOLO cuando es necesario
  const clearCognitoStorage = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('CognitoIdentityServiceProvider')) {
        localStorage.removeItem(key);
      }
    });

    localStorage.removeItem('serviceToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('__suuid__');
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <AWSCognitoContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        registerNew,
        forgotPassword,
        resetPassword,
        changePassword,
        updateProfile,
        verifyAccount,
        resendConfirmationCode,
        userDetails,
        checkEmailExists,
        verifySessionDetails
      }}
    >
      {children}
    </AWSCognitoContext.Provider>
  );
};

export default AWSCognitoContext;
